/* eslint-disable function-call-argument-newline */
import { toContext } from 'ol/render';
import { Style, Fill } from 'ol/style';
import { Polygon, MultiPolygon } from 'ol/geom';
import { DEVICE_PIXEL_RATIO } from 'ol/has';

import {
  IMAGE_LOADING,
  IMAGE_LOADED,
  IMAGE_ERROR,
  DEFAULT_MARK_SIZE
} from '../constants';
import { memoizeStyleFunction, hexToRGB, cloneStyleValue, convertSize } from './styleUtils';
import { getCachedImage, getImageLoadingState } from '../imageCache';
import { imageLoadingPolygonStyle, imageErrorPolygonStyle } from './static';
import { getSimpleStroke, getSimpleFill } from './simpleStyles';
import { getGraphicStrokeRenderer } from './graphicStrokeStyle';
import getMarkFillStyle from './graphicFillStyle';

function createPattern(graphic) {
  const imageUrl = graphic && graphic.externalgraphic && graphic.externalgraphic.onlineresource;
  if (!imageUrl || typeof imageUrl !== 'string') {
    return null;
  }

  const cachedImage = getCachedImage(imageUrl);
  if (!cachedImage || !cachedImage.image || !cachedImage.width || !cachedImage.height) {
    return null;
  }

  const { image, width, height } = cachedImage;
  const numericWidth = Number(width);
  const numericHeight = Number(height);
  if (
    !Number.isFinite(numericWidth) ||
    !Number.isFinite(numericHeight) ||
    numericWidth <= 0 ||
    numericHeight <= 0
  ) {
    return null;
  }
  const cnv = document.createElement('canvas');
  const ctx = cnv.getContext('2d');
  if (!ctx) {
    return null;
  }

  const sizeValue = Number(graphic && graphic.size);
  const safeSize = Number.isFinite(sizeValue) && sizeValue > 0 ? sizeValue : null;
  const rotationValue = Number(graphic && graphic.rotation);
  const safeRotation = Number.isFinite(rotationValue) ? rotationValue : 0;
  const opacityValue = Number(graphic && graphic.opacity);
  const safeOpacity = Number.isFinite(opacityValue) ? opacityValue : 1;

  // Calculate image scale factor.
  let imageRatio = DEVICE_PIXEL_RATIO;
  if (safeSize != null && numericHeight !== safeSize) {
    imageRatio *= safeSize / numericHeight;
  }
  if (!Number.isFinite(imageRatio) || imageRatio <= 0) {
    return null;
  }

  // Draw image to canvas directly if no scaling necessary.
  if (imageRatio === 1) {
    const tempCanvas = document.createElement('canvas');
    const tCtx = tempCanvas.getContext('2d');
    if (!tCtx) {
      return null;
    }
    tempCanvas.width = Math.max(1, Math.round(numericWidth));
    tempCanvas.height = Math.max(1, Math.round(numericHeight));
    tCtx.globalAlpha = safeOpacity;
    tCtx.drawImage(image, 0, 0, numericWidth, numericHeight);
    const ptn = ctx.createPattern(tempCanvas, 'repeat');
    const svgNamespace = 'http://www.w3.org/2000/svg';
    const matrix = document.createElementNS(svgNamespace, 'g').getCTM();
    if (ptn && ptn.setTransform && matrix && Number.isFinite(safeRotation)) {
      ptn.setTransform(matrix.rotate(safeRotation));
    }
    return ptn;
  }

  // Scale the image by drawing onto a temp canvas.
  const tempCanvas = document.createElement('canvas');
  const tCtx = tempCanvas.getContext('2d');
  if (!tCtx) {
    return null;
  }
  const scaledWidth = Math.max(1, Math.round(numericWidth * imageRatio));
  const scaledHeight = Math.max(1, Math.round(numericHeight * imageRatio));
  tempCanvas.width = scaledWidth;
  tempCanvas.height = scaledHeight;
  tCtx.globalAlpha = safeOpacity;
  // prettier-ignore
  tCtx.drawImage(
    image,
    0, 0, numericWidth, numericHeight,
    0, 0, scaledWidth, scaledHeight
  );

  const ptn = ctx.createPattern(tempCanvas, 'repeat');
  const svgNamespace = 'http://www.w3.org/2000/svg';
  const matrix = document.createElementNS(svgNamespace, 'g').getCTM();
  if (ptn && ptn.setTransform && matrix && Number.isFinite(safeRotation)) {
    ptn.setTransform(matrix.rotate(safeRotation));
  }
  return ptn;
}
function getExternalGraphicFill(symbolizer) {
  const { graphic } = symbolizer.fill.graphicfill;
  const fillImageUrl = graphic.externalgraphic.onlineresource;
  if (typeof fillImageUrl !== 'string' || !fillImageUrl) {
    return imageLoadingPolygonStyle.getFill();
  }

  // Use fallback style when graphicfill image hasn't been loaded yet.
  switch (getImageLoadingState(fillImageUrl)) {
    case IMAGE_LOADED:
      {
        const pattern = createPattern(symbolizer.fill.graphicfill.graphic);
        if (!pattern) {
          return imageLoadingPolygonStyle.getFill();
        }
        return new Fill({
          color: pattern,
        });
      }
    case IMAGE_LOADING:
      return imageLoadingPolygonStyle.getFill();
    case IMAGE_ERROR:
      return imageErrorPolygonStyle.getFill();
    default:
      // Load state of an image should be known at this time, but return 'loading' style as fallback.
      return imageLoadingPolygonStyle.getFill();
  }
}

function polygonStyle(symbolizer) {
  const polygonFill = getPolygonFill(symbolizer);

  // When a polygon has a GraphicStroke, use a custom renderer to combine
  // GraphicStroke with fill. This is needed because a custom renderer
  // ignores any stroke, fill and image present in the style.
  if (symbolizer.stroke && symbolizer.stroke.graphicstroke) {
    const renderGraphicStroke = getGraphicStrokeRenderer(symbolizer);
    return new Style({
      renderer: (pixelCoords, renderState) => {
        // First render the fill (if any).
        if (polygonFill) {
          const { feature, context } = renderState;
          const render = toContext(context);
          const normalizedCoords = normalizePixelCoords(
            pixelCoords,
            renderState.pixelRatio || 1
          );
          render.setFillStrokeStyle(polygonFill, undefined);
          const geometryType = feature.getGeometry().getType();
          if (geometryType === 'Polygon') {
            render.drawPolygon(new Polygon(normalizedCoords));
          } else if (geometryType === 'MultiPolygon') {
            render.drawMultiPolygon(new MultiPolygon(normalizedCoords));
          }
        }

        // Then, render the graphic stroke.
        renderGraphicStroke(pixelCoords, renderState);
      },
    });
  }

  const polygonStroke = getSimpleStroke(symbolizer.stroke);

  return new Style({
    fill: polygonFill,
    stroke: polygonStroke,
  });
}

function getPolygonFill(symbolizer) {
  const fillImageUrl = getStaticOnlineResource(
    symbolizer &&
    symbolizer.fill &&
    symbolizer.fill.graphicfill &&
    symbolizer.fill.graphicfill.graphic &&
    symbolizer.fill.graphicfill.graphic.externalgraphic
  );

  const fillMark =
    symbolizer.fill &&
    symbolizer.fill.graphicfill &&
    symbolizer.fill.graphicfill.graphic &&
    symbolizer.fill.graphicfill.graphic.mark;

  const staticGraphicFill = withStaticGraphicSize(
    symbolizer && symbolizer.fill && symbolizer.fill.graphicfill
  );

  let polygonFill = null;
  if (fillImageUrl) {
    polygonFill = getExternalGraphicFill({
      ...symbolizer,
      fill: {
        ...symbolizer.fill,
        graphicfill: staticGraphicFill
      }
    });
  } else if (fillMark) {
    polygonFill = getMarkFillStyle(staticGraphicFill);
  } else {
    polygonFill = getSimpleFill(symbolizer.fill);
  }
  return polygonFill;
}

const cachedPolygonStyle = memoizeStyleFunction(polygonStyle);

/**
 * @private
 * Get an OL line style instance for a feature according to a symbolizer.
 * @param {object} symbolizer SLD symbolizer object.
 * @returns {ol/Style} OpenLayers style instance.
 */
function getPolygonStyle(symbolizer, feature) {
    const olStyle = cloneStyleValue(cachedPolygonStyle(symbolizer));
    const graphicFill = symbolizer?.fill?.graphicfill;

    if (feature.get('xstate') == null || feature.get('xstate') == 0) {
        olStyle.setZIndex(0);
    } else {
        olStyle.setZIndex(100);
    }

    const dynamicGraphicSize = resolveGraphicSize(graphicFill, feature);
    const dynamicGraphicRotation = resolveGraphicRotation(graphicFill, feature);
    const dynamicGraphicOpacity = resolveGraphicOpacity(graphicFill, feature);
    const dynamicGraphicOnlineResource = resolveOnlineResource(
        graphicFill?.graphic?.externalgraphic,
        feature
    );
    const dynamicGraphicFillStyling = resolveGraphicFillStyling(symbolizer, feature);
    const hasGraphicFillMark = Boolean(graphicFill?.graphic?.mark);
    const shouldBuildRuntimeGraphicFill =
        hasGraphicFillMark ||
        dynamicGraphicSize != null ||
        dynamicGraphicRotation != null ||
        dynamicGraphicOpacity != null ||
        dynamicGraphicOnlineResource != null ||
        dynamicGraphicFillStyling != null;
    let runtimePolygonFill = null;
    if (shouldBuildRuntimeGraphicFill) {
        let resolvedSize;
        if (dynamicGraphicSize != null) {
            const numericSize = Number(dynamicGraphicSize);
            if (numericSize == 0)
                numericSize = 8;
            const convertedSize = convertSymbolizerSize(
                numericSize,
                feature,
                symbolizer
            );
            resolvedSize = !Number.isNaN(convertedSize)
                ? convertedSize
                : !Number.isNaN(numericSize)
                    ? numericSize
                    : undefined;
        } else if (hasGraphicFillMark) {
            // For mark-based fills without explicit size, apply UOM conversion to the default mark size.
            const defaultConvertedSize = convertSymbolizerSize(
                DEFAULT_MARK_SIZE,
                feature,
                symbolizer
            );
            resolvedSize = !Number.isNaN(defaultConvertedSize)
                ? defaultConvertedSize
                : DEFAULT_MARK_SIZE;
        }
        const numericRotation = Number(dynamicGraphicRotation);
        const numericOpacity = Number(dynamicGraphicOpacity);
        const resolvedRotation = !Number.isNaN(numericRotation) ? numericRotation : undefined;
        const resolvedOpacity = !Number.isNaN(numericOpacity) ? numericOpacity : undefined;
        let resolvedGraphicFillStyling = dynamicGraphicFillStyling;
        if (resolvedGraphicFillStyling && resolvedGraphicFillStyling.strokeWidth != null) {
            const numericFillStrokeWidth = Number(resolvedGraphicFillStyling.strokeWidth);
            const convertedFillStrokeWidth = convertSymbolizerSize(
                numericFillStrokeWidth,
                feature,
                symbolizer
            );
            resolvedGraphicFillStyling = {
                ...resolvedGraphicFillStyling,
                strokeWidth: !Number.isNaN(convertedFillStrokeWidth)
                    ? convertedFillStrokeWidth
                    : resolvedGraphicFillStyling.strokeWidth
            };
        }
        const dynamicGraphicFill = buildGraphicFillFromOverrides(
            symbolizer,
            resolvedSize,
            resolvedRotation,
            resolvedOpacity,
            dynamicGraphicOnlineResource,
            resolvedGraphicFillStyling
        );
        if (dynamicGraphicFill) {
            runtimePolygonFill = dynamicGraphicFill;
            if (olStyle.setFill) {
                olStyle.setFill(dynamicGraphicFill);
            }
        }
    }
    if (!runtimePolygonFill) {
        runtimePolygonFill = getPolygonFill(symbolizer);
    }

    if (symbolizer?.stroke?.graphicstroke) {
        const dynamicGraphicStrokeSymbolizer = buildGraphicStrokeFromOverrides(symbolizer, feature) || symbolizer;
        const renderGraphicStroke = getGraphicStrokeRenderer(dynamicGraphicStrokeSymbolizer);
        return new Style({
            zIndex: olStyle.getZIndex ? olStyle.getZIndex() : undefined,
            renderer: (pixelCoords, renderState) => {
                if (runtimePolygonFill) {
                    const { feature: renderFeature, context } = renderState;
                    const render = toContext(context);
                    const normalizedCoords = normalizePixelCoords(
                        pixelCoords,
                        renderState.pixelRatio || 1
                    );
                    render.setFillStrokeStyle(runtimePolygonFill, undefined);
                    const geometryType = renderFeature.getGeometry().getType();
                    if (geometryType === 'Polygon') {
                        render.drawPolygon(new Polygon(normalizedCoords));
                    } else if (geometryType === 'MultiPolygon') {
                        render.drawMultiPolygon(new MultiPolygon(normalizedCoords));
                    }
                }

                renderGraphicStroke(pixelCoords, renderState);
            },
        });
    }

    const strokeStyling = symbolizer?.stroke?.styling;
    if (strokeStyling && (strokeStyling.stroke || strokeStyling.strokeOpacity || strokeStyling.strokeWidth || strokeStyling.strokeDasharray || strokeStyling.strokeDashoffset)) {
        let olStroke = olStyle.getStroke && olStyle.getStroke();
        if (!olStroke) {
            olStroke = getSimpleStroke({
                styling: {
                    stroke: '#000000',
                    strokeWidth: 1
                }
            });
            if (olStroke && olStyle.setStroke) {
                olStyle.setStroke(olStroke);
            }
        }
        if (olStroke) {
            const strokeValue = resolveValue(strokeStyling.stroke, feature);
            const strokeOpacityValue = resolveValue(strokeStyling.strokeOpacity, feature);
            const strokeDasharrayValue = resolveValue(strokeStyling.strokeDasharray, feature);
            const strokeDashoffsetValue = resolveValue(strokeStyling.strokeDashoffset, feature);

            const strokeColor = buildColor(strokeValue, strokeOpacityValue);
            if (strokeColor != null) {
                olStroke.setColor(strokeColor);
            } else if (strokeOpacityValue || strokeOpacityValue === 0) {
                const currentColor = olStroke.getColor();
                if (isHexColor(currentColor)) {
                    olStroke.setColor(hexToRGB(currentColor, strokeOpacityValue));
                }
            }

            const strokeWidthValue = resolveValue(strokeStyling.strokeWidth, feature);
            const defaultStrokeWidth = resolveNumericOrDefault(strokeStyling.strokeWidth, 1);
            const resolvedStrokeWidth = resolveNumericOrDefault(strokeWidthValue, defaultStrokeWidth);
            const convertedStrokeWidth = convertSymbolizerSize(
                resolvedStrokeWidth,
                feature,
                symbolizer
            );
            const finalStrokeWidth = Number.isNaN(convertedStrokeWidth)
                ? resolvedStrokeWidth
                : convertedStrokeWidth;
            olStroke.setWidth(finalStrokeWidth);

            if (strokeDasharrayValue != null) {
                const rawLineDash = Array.isArray(strokeDasharrayValue)
                    ? strokeDasharrayValue
                    : String(strokeDasharrayValue).trim().split(/[\s,]+/);
                const lineDash = rawLineDash
                    .map(entry => {
                        const numericEntry = Number(entry);
                        if (Number.isNaN(numericEntry)) {
                            return null;
                        }
                        const convertedEntry = convertSymbolizerSize(
                            numericEntry,
                            feature,
                            symbolizer
                        );
                        return Number.isNaN(convertedEntry) ? numericEntry : convertedEntry;
                    })
                    .filter(entry => entry != null);
                olStroke.setLineDash(lineDash);
            }

            const numericDashOffset = Number(strokeDashoffsetValue);
            const convertedDashOffset = convertSymbolizerSize(
                numericDashOffset,
                feature,
                symbolizer
            );
            if (!Number.isNaN(convertedDashOffset)) {
                olStroke.setLineDashOffset(convertedDashOffset);
            }
        }
    }

    const fillStyling = symbolizer?.fill?.styling;
    if (fillStyling && (fillStyling.fill || fillStyling.fillOpacity)) {
        const fillValue = resolveValue(fillStyling.fill, feature);
        const fillOpacityValue = resolveValue(fillStyling.fillOpacity, feature);
        const olFill = olStyle.getFill && olStyle.getFill();

        if (olFill) {
            const fillColor = buildColor(fillValue, fillOpacityValue);
            if (fillColor != null) {
                olFill.setColor(fillColor);
            } else if (fillOpacityValue || fillOpacityValue === 0) {
                const currentColor = olFill.getColor();
                if (isHexColor(currentColor)) {
                    olFill.setColor(hexToRGB(currentColor, fillOpacityValue));
                }
            }
        }
    }

    return olStyle;
}

function resolveValue(value, feature) {
    if (value && value.prop) {
        return feature.get(value.prop);
    }
    if (value && value.type === 'propertyname') {
        return feature.get(value.value);
    }
    return value;
}

function resolveGraphicSize(graphicFill, feature) {
    const sizeValue = graphicFill?.graphic?.size;
    if (sizeValue == null) {
        return null;
    }
    if (sizeValue && sizeValue.prop) {
        return feature.get(sizeValue.prop);
    }
    if (sizeValue && sizeValue.type === 'propertyname') {
        return feature.get(sizeValue.value);
    }
    return sizeValue;
}

function resolveGraphicRotation(graphicFill, feature) {
    const rotationValue = graphicFill?.graphic?.rotation;
    if (rotationValue == null) {
        return null;
    }
    return resolveValue(rotationValue, feature);
}

function resolveGraphicOpacity(graphicFill, feature) {
    const opacityValue = graphicFill?.graphic?.opacity;
    if (opacityValue == null) {
        return null;
    }
    return resolveValue(opacityValue, feature);
}

function resolveOnlineResource(externalgraphic, feature) {
    const source = externalgraphic?.onlineresource;
    if (source == null || source === '') {
        return null;
    }
    if (typeof source === 'string') {
        return normalizeOnlineResource(source);
    }
    if (source.prop) {
        return normalizeOnlineResource(feature.get(source.prop), true);
    }
    if (source.type === 'propertyname') {
        return normalizeOnlineResource(feature.get(source.value), true);
    }
    return normalizeOnlineResource(
        source.value != null ? feature.get(source.value) : source,
        true
    );
}

function normalizeOnlineResource(value, useFallback = false) {
    if (value == null) {
        return useFallback ? getDefaultOnlineResourceUrl() : null;
    }
    const normalized = String(value).trim();
    if (normalized === '') {
        return useFallback ? getDefaultOnlineResourceUrl() : null;
    }
    return normalized;
}

function getStaticOnlineResource(externalgraphic) {
    const source = externalgraphic?.onlineresource;
    if (typeof source !== 'string') {
        return null;
    }
    return source;
}

function resolveGraphicFillStyling(symbolizer, feature) {
    const stylingFill = symbolizer?.fill?.graphicfill?.graphic?.mark?.fill?.styling;
    const stylingStroke = symbolizer?.fill?.graphicfill?.graphic?.mark?.stroke?.styling;
    if (!stylingFill && !stylingStroke) {
        return null;
    }

    const fillValue = resolveValue(stylingFill?.fill, feature);
    const fillOpacityValue = resolveValue(stylingFill?.fillOpacity, feature);
    const strokeValue = resolveValue(stylingStroke?.stroke, feature);
    const strokeOpacityValue = resolveValue(stylingStroke?.strokeOpacity, feature);
    const strokeWidthValue = resolveValue(stylingStroke?.strokeWidth, feature);

    if (
        fillValue == null &&
        fillOpacityValue == null &&
        strokeValue == null &&
        strokeOpacityValue == null &&
        strokeWidthValue == null
    ) {
        return null;
    }

    return {
        fill: fillValue,
        fillOpacity: fillOpacityValue,
        stroke: strokeValue,
        strokeOpacity: strokeOpacityValue,
        strokeWidth: strokeWidthValue
    };
}

function withStaticGraphicSize(graphicFill) {
    if (!graphicFill || !graphicFill.graphic) {
        return graphicFill;
    }
    const staticSize = resolveStaticGraphicSize(graphicFill.graphic.size);
    return {
        ...graphicFill,
        graphic: {
            ...graphicFill.graphic,
            size: staticSize
        }
    };
}

function resolveStaticGraphicSize(sizeValue) {
    if (sizeValue && (sizeValue.prop || sizeValue.type === 'propertyname')) {
        return DEFAULT_MARK_SIZE;
    }
    return sizeValue != null ? sizeValue : DEFAULT_MARK_SIZE;
}

function buildGraphicFillFromOverrides(symbolizer, size, rotation, opacity, onlineresource, graphicFillStyling) {
    const graphicFill = symbolizer?.fill?.graphicfill;
    const graphic = graphicFill?.graphic;
    if (!graphic) {
        return null;
    }

    const fillStyling = {
        ...graphic.mark?.fill?.styling
    };
    if (graphicFillStyling?.fill != null) {
        fillStyling.fill = graphicFillStyling.fill;
    }
    if (graphicFillStyling?.fillOpacity != null) {
        fillStyling.fillOpacity = graphicFillStyling.fillOpacity;
    }

    const strokeStyling = {
        ...graphic.mark?.stroke?.styling
    };
    if (graphicFillStyling?.stroke != null) {
        strokeStyling.stroke = graphicFillStyling.stroke;
    }
    if (graphicFillStyling?.strokeOpacity != null) {
        strokeStyling.strokeOpacity = graphicFillStyling.strokeOpacity;
    }
    if (graphicFillStyling?.strokeWidth != null) {
        strokeStyling.strokeWidth = graphicFillStyling.strokeWidth;
    }

    const sizedGraphicFill = {
        ...graphicFill,
        graphic: {
            ...graphic,
            size: size != null ? size : graphic.size,
            rotation: rotation != null ? rotation : graphic.rotation,
            opacity: opacity != null ? opacity : graphic.opacity,
            externalgraphic: graphic.externalgraphic
                ? {
                    ...graphic.externalgraphic,
                    onlineresource: onlineresource != null
                        ? onlineresource
                        : graphic.externalgraphic.onlineresource
                }
                : graphic.externalgraphic,
            mark: graphic.mark && graphicFillStyling
                ? {
                    ...graphic.mark,
                    fill: {
                        ...graphic.mark.fill,
                        styling: fillStyling
                    },
                    stroke: {
                        ...graphic.mark.stroke,
                        styling: strokeStyling
                    },
                }
                : graphic.mark
        }
    };

    if (graphic.externalgraphic && graphic.externalgraphic.onlineresource) {
        return getExternalGraphicFill({
            fill: {
                graphicfill: sizedGraphicFill
            }
        });
    }

    if (graphic.mark) {
        return getMarkFillStyle(sizedGraphicFill);
    }

    return null;
}

function buildGraphicStrokeFromOverrides(symbolizer, feature) {
    const graphicstroke = symbolizer?.stroke?.graphicstroke;
    const graphic = graphicstroke?.graphic;
    if (!graphicstroke || !graphic) {
        return null;
    }

    const sizeValue = resolveValue(graphic.size, feature);
    const rotationValue = resolveValue(graphic.rotation, feature);
    const opacityValue = resolveValue(graphic.opacity, feature);
    const onlineresourceValue = resolveOnlineResource(graphic.externalgraphic, feature);

    const fillStyling = graphic?.mark?.fill?.styling || {};
    const strokeStyling = graphic?.mark?.stroke?.styling || {};
    const fillValue = resolveValue(fillStyling.fill, feature);
    const fillOpacityValue = resolveValue(fillStyling.fillOpacity, feature);
    const strokeValue = resolveValue(strokeStyling.stroke, feature);
    const strokeOpacityValue = resolveValue(strokeStyling.strokeOpacity, feature);
    const strokeWidthValue = resolveValue(strokeStyling.strokeWidth, feature);

    if (
        sizeValue == null &&
        rotationValue == null &&
        opacityValue == null &&
        onlineresourceValue == null &&
        fillValue == null &&
        fillOpacityValue == null &&
        strokeValue == null &&
        strokeOpacityValue == null &&
        strokeWidthValue == null
    ) {
        return null;
    }

    const nextFillStyling = {
        ...fillStyling
    };
    if (fillValue != null) {
        nextFillStyling.fill = fillValue;
    }
    if (fillOpacityValue != null) {
        nextFillStyling.fillOpacity = fillOpacityValue;
    }

    const nextStrokeStyling = {
        ...strokeStyling
    };
    if (strokeValue != null) {
        nextStrokeStyling.stroke = strokeValue;
    }
    if (strokeOpacityValue != null) {
        nextStrokeStyling.strokeOpacity = strokeOpacityValue;
    }
    if (strokeWidthValue != null) {
        nextStrokeStyling.strokeWidth = strokeWidthValue;
    }

    return {
        ...symbolizer,
        stroke: {
            ...symbolizer.stroke,
            graphicstroke: {
                ...graphicstroke,
                graphic: {
                    ...graphic,
                    size: sizeValue != null ? sizeValue : graphic.size,
                    rotation: rotationValue != null ? rotationValue : graphic.rotation,
                    opacity: opacityValue != null ? opacityValue : graphic.opacity,
                    externalgraphic: graphic.externalgraphic
                        ? {
                            ...graphic.externalgraphic,
                            onlineresource: onlineresourceValue != null
                                ? onlineresourceValue
                                : graphic.externalgraphic.onlineresource
                        }
                        : graphic.externalgraphic,
                    mark: graphic.mark
                        ? {
                            ...graphic.mark,
                            fill: {
                                ...graphic.mark.fill,
                                styling: nextFillStyling
                            },
                            stroke: {
                                ...graphic.mark.stroke,
                                styling: nextStrokeStyling
                            }
                        }
                        : graphic.mark
                }
            }
        }
    };
}

function buildColor(value, opacity) {
    if ((opacity || opacity === 0) && isHexColor(value)) {
        return hexToRGB(value, opacity);
    }
    return value;
}

function resolveNumericOrDefault(value, fallback) {
    if (value == null || value === '') {
        return fallback;
    }
    if (typeof value === 'object') {
        return fallback;
    }
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? fallback : numericValue;
}

function convertSymbolizerSize(value, feature, symbolizer) {
    return convertSize(
        Number(value),
        feature.get('currentResolution'),
        feature.get('currentUom'),
        symbolizer && symbolizer.uom
    );
}

function isHexColor(value) {
    return value && value.slice && value.slice(0, 1) === '#';
}

function normalizePixelCoords(pixelCoords, pixelRatio) {
    if (!pixelCoords || pixelRatio === 1) {
        return pixelCoords;
    }
    return pixelCoords.map(coordOrRing => {
        if (Array.isArray(coordOrRing[0])) {
            return normalizePixelCoords(coordOrRing, pixelRatio);
        }
        return [coordOrRing[0] / pixelRatio, coordOrRing[1] / pixelRatio];
    });
}

function getDefaultOnlineResourceUrl() {
    return rootURL + 'img/sld/unknown_img.png';
}

export default getPolygonStyle;



